// Active media owns playback. All listeners and observers end with the element.
export class FDMediaGallery extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.slides = [...this.querySelectorAll('[data-slide]')]; this.index = 0;
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (button?.matches('[data-prev]')) this.select(this.index - 1);
      if (button?.matches('[data-next]')) this.select(this.index + 1);
      if (button?.matches('[data-select]')) this.select(Number(button.dataset.select));
    }, options);
    this.querySelector('.fd-gallery__stage')?.addEventListener('keydown', (event) => {
      if (event.target.matches('input,select,video')) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1)); }
    }, options);
    bindSwipe(this.querySelector('.fd-gallery__stage'), (direction) => this.select(this.index + direction), options);
    this.select(0);
  }
  select(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      slide.hidden = i !== this.index;
      slide.querySelectorAll('fd-film').forEach((film) => { film.active = i === this.index; film.reconcile?.(); });
    });
    this.querySelectorAll('[data-select]').forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.select) === this.index)));
    const counter = this.querySelector('[data-counter]');
    if (counter) counter.textContent = `${this.index + 1} / ${this.slides.length}`;
  }
  disconnectedCallback() { this.abort?.abort(); }
}
export function bindSwipe(surface, move, options) {
  let start;
  surface?.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' || event.target.closest('button,video,model-viewer')) return;
    start = { x: event.clientX, y: event.clientY };
  }, options);
  surface?.addEventListener('pointerup', (event) => {
    if (!start) return;
    const dx = event.clientX - start.x, dy = event.clientY - start.y; start = null;
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      move(dx < 0 ? 1 : -1);
      surface.addEventListener('click', (click) => { click.preventDefault(); click.stopPropagation(); }, { once: true, capture: true, signal: options.signal });
    }
  }, options);
  surface?.addEventListener('pointercancel', () => { start = null; }, options);
}
export function allowsAutoplay({ active, visible, hidden, reduced, saveData, effectiveType, userPaused, failed, cinematic }) {
  return Boolean(active && visible && !hidden && !reduced && !saveData && !['slow-2g', '2g'].includes(effectiveType) && !userPaused && !failed && cinematic);
}
export class FDFilm extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    this.video = this.querySelector('video'); this.toggle = this.querySelector('button');
    if (!this.video || !this.toggle) return;
    this.active = !this.closest('[data-slide]')?.hidden; this.visible = false;
    this.userPaused ??= false; this.failed = false; this.manual = false;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.connection = navigator.connection; this.cinematic = this.hasAttribute('data-cinematic');
    const options = { signal: this.abort.signal };
    this.toggle.addEventListener('click', () => {
      if (!this.video.paused || this.pending) { this.userPaused = true; this.manual = false; this.pause(); }
      else { this.userPaused = false; this.failed = false; this.manual = true; this.start(true); }
    }, options);
    document.addEventListener('visibilitychange', () => this.reconcile(), options);
    this.motion.addEventListener('change', () => this.reconcile(), options);
    this.connection?.addEventListener('change', () => this.reconcile(), options);
    document.addEventListener('fd:editorial-playing', () => { if (this.cinematic) this.pause(); }, options);
    this.video.addEventListener('playing', () => this.reveal(), options);
    this.video.addEventListener('pause', () => {
      if (!this.programmaticPause && !this.video.ended) { this.userPaused = true; this.manual = false; }
      this.programmaticPause = false; this.setLabel(false);
    }, options);
    this.video.addEventListener('play', () => {
      if (!this.pending) { this.userPaused = false; this.manual = true; this.failed = false; }
      if (!this.eligibleToContinue()) this.pause();
    }, options);
    this.video.addEventListener('error', () => this.fail(), options);
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.intersectionRatio >= 0.5; this.reconcile();
    }, { threshold: [0, 0.5, 1] });
    this.observer.observe(this);
  }
  eligibleToContinue() { return this.active && this.visible && !document.hidden && !this.userPaused; }
  canAutoplay() {
    return allowsAutoplay({ active: this.active, visible: this.visible, hidden: document.hidden, reduced: this.motion.matches, saveData: this.connection?.saveData, effectiveType: this.connection?.effectiveType, userPaused: this.userPaused, failed: this.failed, cinematic: this.cinematic });
  }
  reconcile() {
    if (!this.video) return;
    if (!this.eligibleToContinue() || (!this.manual && !this.canAutoplay())) { this.pause(); return; }
    if ((this.manual || this.canAutoplay()) && this.video.paused && !this.pending) this.start(false);
  }
  hydrate() {
    if (this.video.getAttribute('src')) return;
    const sources = [...this.video.querySelectorAll('source[data-src]')].filter((source) => this.video.canPlayType(source.type));
    const target = Math.min(1080, Math.max(720, this.clientHeight * Math.min(devicePixelRatio || 1, 1.5)));
    const source = sources.find((item) => Number(item.dataset.height) >= target) || sources.at(-1);
    if (!source) throw new Error('No supported video source');
    this.video.src = source.dataset.src;
    this.video.preload = 'metadata'; this.video.load();
  }
  async start(explicit) {
    if (this.pending || !this.active || document.hidden) return;
    if (!explicit && !this.eligibleToContinue()) return;
    this.pending = true; this.setLabel(true);
    this.querySelector('.fd-film__status').textContent = '';
    const generation = this.generation = (this.generation || 0) + 1;
    try {
      this.hydrate();
      // Explicit activation may start before the observer's first delivery.
      if (explicit) this.visible = true;
      await this.video.play();
      if (generation !== this.generation || !this.eligibleToContinue()) this.pause();
    } catch (error) {
      if (generation === this.generation && error.name !== 'AbortError') this.fail();
    } finally { if (generation === this.generation) this.pending = false; }
  }
  reveal() {
    const show = () => {
      if (!this.isConnected || this.video.paused || !this.eligibleToContinue() || this.video.readyState < 2) return;
      this.classList.add('fd-film--playing'); this.video.controls = !this.cinematic;
      if (!this.cinematic && document.activeElement === this.toggle) { this.video.tabIndex = 0; this.video.focus(); }
      this.setLabel(true);
      if (!this.cinematic) document.dispatchEvent(new CustomEvent('fd:editorial-playing', { detail: this }));
    };
    if (this.video.requestVideoFrameCallback) this.frame = this.video.requestVideoFrameCallback(show);
    else { this.raf = requestAnimationFrame(show); }
  }
  setLabel(playing) {
    this.toggle.textContent = playing ? this.dataset.pauseLabel : this.dataset.playLabel;
    this.toggle.setAttribute('aria-label', this.toggle.textContent);
  }
  pause() {
    this.generation = (this.generation || 0) + 1; this.pending = false;
    if (this.frame) this.video.cancelVideoFrameCallback?.(this.frame);
    cancelAnimationFrame(this.raf);
    if (!this.video.paused) { this.programmaticPause = true; this.video.pause(); }
    this.setLabel(false);
  }
  fail() {
    this.failed = true; this.manual = false; this.pause();
    this.classList.remove('fd-film--playing'); this.video.controls = false;
    this.querySelector('.fd-film__status').textContent = this.dataset.errorLabel;
  }
  disconnectedCallback() { this.pause(); this.abort?.abort(); this.observer?.disconnect(); }
}
export class FDStillLife extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    this.slides = [...this.querySelectorAll('[data-still]')]; this.index = 0;
    this.desktop = matchMedia('(min-width: 64em)');
    const options = { signal: this.abort.signal };
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-prev]')) this.select(this.index - 1);
      if (event.target.closest('[data-next]')) this.select(this.index + 1);
    }, options);
    bindSwipe(this.querySelector('.fd-stills__images'), (direction) => this.select(this.index + direction), options);
    this.desktop.addEventListener('change', () => this.select(this.index), options);
    this.select(0);
  }
  select(index) {
    if (!this.slides.length) return;
    const next = (index + this.slides.length) % this.slides.length;
    const generation = this.generation = (this.generation || 0) + 1;
    const picture = this.slides[next].querySelector?.('img');
    if (!this.desktop.matches && next !== this.index && picture) {
      // Only the requested image is promoted. Retain the current frame until decode succeeds.
      picture.loading = 'eager';
      this.setAttribute('aria-busy', 'true');
      picture.decode().then(() => {
        if (generation === this.generation && this.isConnected) this.show(next);
      }).catch(() => {
        if (generation === this.generation) {
          const status = this.querySelector('[data-still-status]');
          if (status) status.textContent = this.dataset.error;
        }
      }).finally(() => { if (generation === this.generation) this.removeAttribute('aria-busy'); });
      return;
    }
    this.show(next);
  }
  show(index) {
    this.removeAttribute?.('aria-busy');
    this.index = index;
    const status = this.querySelector('[data-still-status]');
    if (status) status.textContent = '';
    this.slides.forEach((slide, i) => { slide.hidden = !this.desktop.matches && i !== this.index; });
    const counter = this.querySelector('[data-counter]');
    if (counter) counter.textContent = `${this.index + 1} / ${this.slides.length}`;
  }
  disconnectedCallback() { this.generation = (this.generation || 0) + 1; this.abort?.abort(); }
}
for (const [name, definition] of [['fd-media-gallery', FDMediaGallery], ['fd-film', FDFilm], ['fd-still-life', FDStillLife]]) {
  if (!customElements.get(name)) customElements.define(name, definition);
}
